import type {ReactElement} from 'react';
import React from 'react';
import BottomBar from '@Components/contextual-menu/components/bottom-bar';
import {openPosterEditorItemPositionModal} from '@Modals/poster-editor-item-position-modal/poster-editor-item-position-modal';
import {
  deleteItemByID,
  duplicateActiveItems,
  onCopyStylesClicked,
  showCopyStylesAsNewFeature,
  showEditorNotificationMessageGrowl,
  toggleLockStateForSelectedItem,
} from '@Components/poster-editor/library/poster-editor-library';
import {openPosterEditorItemOpacityModal} from '@Modals/poster-editor-item-opacity-modal';
import {openPosterEditorItemFontStyleModal} from '@Modals/poster-editor-item-font-style-modal';
import {openPosterEditorHyperlinkModal} from '@Modals/poster-editor-item-hyperlink-modal';
import {openPosterEditorItemBackgroundPanel} from '@Modals/poster-editor-item-background-modal';
import {openPosterEditorAlignmentModal} from '@Modals/poster-editor-item-alignment-modal';
import {openPosterEditorTextOutlineModal} from '@Modals/poster-editor-text-outline-modal';
import {openPosterEditorNumberOfTabsModal} from '@Modals/poster-editor-number-of-tabs-modal';
import {openPosterEditorTabsTextModal} from '@Modals/poster-editor-tabs-text-modal';
import {openPosterEditorLayoutFillModal} from '@Modals/poster-editor-layout-fill-modal';
import {openPosterEditorTextItemFontModal} from '@Modals/poster-editor-text-item-font-modal';
import {openPosterEditorTabItemSeparatorModal} from '@Modals/poster-editor-tab-item-separator-modal';
import {useEditorPrefetchTabsMobileModules} from '@Hooks/poster-editor/useEditorPrefetchTabsMobileModules';
import {openPosterEditorLineHeightModal} from '@Modals/poster-editor-item-line-height-modal';
import {openPosterEditorItemLetterSpacingModal} from '@Modals/poster-editor-item-letter-spacing-modal';
import {TabItemBottomBarOptions} from '@Components/poster-editor/components/poster-editor-bottom-bar/poster-editor-bottom-bar.types';
import {useAppSelector} from '@/hooks';
import {getCopiedStyles} from '@Components/poster-editor/poster-editor-reducer';
import {CopyStyleState} from '@Components/poster-editor/components/poster-editing-side-panel/components/poster-item-controls/poster-item-controls.types';
import {useSingleActivePosterItemId} from '@Hooks/poster-editor/useSingleActivePosterItemId';
import {useGetKeyFromActiveItem} from '@Hooks/poster-editor/useGetKeyFromActiveItem';
import {ItemColorFill} from '@Components/poster-editor/components/poster-editor-bottom-bar/components/item-color-fill/item-color-fill';
import {useGetKeyFromTextStylesActiveItem} from '@Hooks/poster-editor/useGetKeyFromTextStylesActiveItem';
import {PulsatingDot} from '@Components/poster-editor/components/pulsating-dot';
import styles from './tabs-bottom-bar.module.scss';
import {openItemAnimationModal} from '@Modals/poster-editor-item-animation-modal';

export function TabsBottomBar(): ReactElement<any> | null {
  const copiedStylesState = useAppSelector(getCopiedStyles) ? CopyStyleState.COPIED : CopyStyleState.COPY;
  const activeItemId = useSingleActivePosterItemId();
  const activeItem = activeItemId ? window.posterEditor?.whiteboard?.getItemForId(activeItemId) : undefined;
  const isLocked = useGetKeyFromActiveItem('lockMovementX');
  const isBold = useGetKeyFromTextStylesActiveItem('isBold');
  useEditorPrefetchTabsMobileModules();

  if (!activeItem?.isTab()) {
    return null;
  }

  const showDisabledOutlineMessageGrowl = (): void => {
    showEditorNotificationMessageGrowl(window.i18next.t('pmwjs_disabled_text_outline_mobile_text'));
  };

  const pulsatingDotForCopyStyles = (): ReactElement<any> | null => {
    if (showCopyStylesAsNewFeature()) {
      return <PulsatingDot containerClassname={`_position-absolute  ${styles.pulsatingDotForCopyStyles}`} />;
    }
    return null;
  };

  const onClickHandler = (itemId: TabItemBottomBarOptions): void => {
    switch (itemId) {
      case TabItemBottomBarOptions.POSITION:
        openPosterEditorItemPositionModal();
        break;
      case TabItemBottomBarOptions.NUM_OF_TABS:
        openPosterEditorNumberOfTabsModal();
        break;
      case TabItemBottomBarOptions.TEXT:
        openPosterEditorTabsTextModal();
        break;
      case TabItemBottomBarOptions.FONT:
        openPosterEditorTextItemFontModal();
        break;
      case TabItemBottomBarOptions.STYLE:
        openPosterEditorItemFontStyleModal();
        break;
      case TabItemBottomBarOptions.DUPLICATE:
        duplicateActiveItems();
        break;
      case TabItemBottomBarOptions.FILL:
        openPosterEditorLayoutFillModal();
        break;
      case TabItemBottomBarOptions.ALIGNMENT:
        openPosterEditorAlignmentModal();
        break;
      case TabItemBottomBarOptions.OUTLINE:
        if (isBold) {
          showDisabledOutlineMessageGrowl();
          return;
        }
        openPosterEditorTextOutlineModal();
        break;
      case TabItemBottomBarOptions.LINE_HEIGHT:
        openPosterEditorLineHeightModal();
        break;
      case TabItemBottomBarOptions.LETTER_SPACING:
        openPosterEditorItemLetterSpacingModal();
        break;
      case TabItemBottomBarOptions.BACKGROUND:
        openPosterEditorItemBackgroundPanel();
        break;
      case TabItemBottomBarOptions.SEPERATOR:
        openPosterEditorTabItemSeparatorModal();
        break;
      case TabItemBottomBarOptions.COPY_STYLES:
        onCopyStylesClicked();
        break;
      case TabItemBottomBarOptions.HYPERLINK:
        openPosterEditorHyperlinkModal();
        break;
      case TabItemBottomBarOptions.OPACITY:
        openPosterEditorItemOpacityModal();
        break;
      case TabItemBottomBarOptions.ANIMATION:
        openItemAnimationModal();
        break;
      case TabItemBottomBarOptions.LOCK:
        toggleLockStateForSelectedItem();
        break;
      case TabItemBottomBarOptions.DELETE:
        deleteItemByID(activeItem.uid);
        break;
      default:
    }
  };

  const getCustomFillContent = (): ReactElement<any> => {
    return <ItemColorFill />;
  };

  return (
    <BottomBar
      id="poster-editing-default-bottom-bar"
      onClick={onClickHandler}
      items={[
        {
          id: TabItemBottomBarOptions.NUM_OF_TABS,
          icon: 'icon-number-of-tabs',
          text: window.i18next.t('pmwjs_no_of_tabs'),
          textClasses: 'spacing-p-0',
        },
        {
          id: TabItemBottomBarOptions.TEXT,
          icon: 'icon-text',
          text: window.i18next.t('pmwjs_text'),
        },
        {
          id: TabItemBottomBarOptions.FONT,
          icon: 'icon-font',
          text: window.i18next.t('pmwjs_font'),
        },
        {
          id: TabItemBottomBarOptions.FILL,
          customContent: getCustomFillContent(),
          text: window.i18next.t('pmwjs_fill'),
        },
        {
          id: TabItemBottomBarOptions.STYLE,
          icon: 'icon-text-style',
          text: window.i18next.t('pmwjs_menu_stlye'),
        },
        {
          id: TabItemBottomBarOptions.DUPLICATE,
          icon: 'icon-copy',
          text: window.i18next.t('pmwjs_duplicate'),
        },
        {
          id: TabItemBottomBarOptions.POSITION,
          icon: 'icon-prints-stack',
          text: window.i18next.t('pmwjs_position'),
        },
        {
          id: TabItemBottomBarOptions.OUTLINE,
          icon: 'icon-outline',
          text: window.i18next.t('pmwjs_outline'),
          isDisabled: isBold,
        },
        {
          id: TabItemBottomBarOptions.ALIGNMENT,
          icon: 'icon-h-align-center',
          text: window.i18next.t('pmwjs_alignment'),
        },
        {
          id: TabItemBottomBarOptions.LINE_HEIGHT,
          icon: 'icon-line-height',
          text: window.i18next.t('pmwjs_line_height'),
        },
        {
          id: TabItemBottomBarOptions.LETTER_SPACING,
          icon: 'icon-letter-spacing',
          text: window.i18next.t('pmwjs_letter_spacing'),
        },
        {
          id: TabItemBottomBarOptions.BACKGROUND,
          icon: 'icon-background',
          text: window.i18next.t('pmwjs_background'),
        },
        {
          id: TabItemBottomBarOptions.SEPERATOR,
          icon: 'icon-divider',
          text: window.i18next.t('pmwjs_separator'),
        },
        {
          id: TabItemBottomBarOptions.COPY_STYLES,
          icon: copiedStylesState === CopyStyleState.COPY ? 'icon-copy-style-brush' : 'icon-paste-style-brush',
          text: copiedStylesState === CopyStyleState.COPY ? window.i18next.t('pmwjs_copy_style') : window.i18next.t('pmwjs_paste_style'),
          isSelected: copiedStylesState === CopyStyleState.COPIED,
          tag: pulsatingDotForCopyStyles(),
        },
        {
          id: TabItemBottomBarOptions.ANIMATION,
          icon: 'icon-animation',
          text: window.i18next.t('pmwjs_animation'),
        },
        {
          id: TabItemBottomBarOptions.HYPERLINK,
          icon: 'icon-link',
          text: window.i18next.t('pmwjs_hyperlink'),
        },
        {
          id: TabItemBottomBarOptions.OPACITY,
          icon: 'icon-opacity',
          text: window.i18next.t('pmwjs_opacity'),
        },
        {
          id: TabItemBottomBarOptions.LOCK,
          icon: isLocked ? 'icon-lock' : 'icon-unlock',
          text: isLocked ? window.i18next.t('pmwjs_unlock') : window.i18next.t('pmwjs_lock'),
        },
        {
          id: TabItemBottomBarOptions.DELETE,
          icon: 'icon-delete',
          text: window.i18next.t('pmwjs_delete'),
          className: 'color-danger-100',
        },
      ]}
    />
  );
}
